import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { fetchApi } from '../api';
import { CHANNELS } from '../constants/channels';
import { formatDate, formatNumber, useUserSettings } from '../utils/userSettings';
import '../styles/dashboard.css';

/**
 * Every number on this screen is measured over the selected period, for this
 * tenant, or it is not shown at all.
 *
 * What used to be here instead:
 *  - four sparklines with hard-coded SVG coordinates, identical for every account;
 *  - a "Лиды по дням" chart that counted inbound *messages*, so one talkative
 *    customer read as twenty leads;
 *  - "Конверсия в заказ" computed over all history and unaffected by the period
 *    switch above it;
 *  - a banner promising "+30% обращений" and naming Avito and Viber as missing
 *    whether or not they were connected.
 */

interface DashboardProps {
  setActiveView: (view: string) => void;
}

interface MetricSeries {
  value: number;
  spark: number[] | null;
}

interface ConversionMetric {
  value: number | null;
  leads: number;
  converted: number;
  spark: (number | null)[] | null;
}

interface DashboardStats {
  period: { days: number; from: string; to: string; timezone: string };
  metrics: {
    leads: MetricSeries;
    aiMessages: MetricSeries;
    revenue: MetricSeries;
    conversion: ConversionMetric;
  };
  series: { d: string; n: number }[];
  byChannel: { type: string; n: number }[];
  channels: { connected: string[]; total: number };
}

const RANGES = [
  { value: '7', chip: '7 дней', label: 'за 7 дней' },
  { value: '14', chip: '14 дней', label: 'за 14 дней' },
  { value: '30', chip: '30 дней', label: 'за 30 дней' },
];

const NOT_ENOUGH_DATA = 'Недостаточно данных для графика';

function periodLabel(days: number): string {
  const known = RANGES.find((range) => range.value === String(days));
  return known ? known.label : `за ${days} дн.`;
}

function plural(count: number, forms: [string, string, string]): string {
  const abs = Math.abs(count) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return forms[2];
  if (last > 1 && last < 5) return forms[1];
  if (last === 1) return forms[0];
  return forms[2];
}

/**
 * A sparkline drawn from the real series behind the KPI above it. `null` entries
 * are gaps (a day the metric is undefined for), not zeroes, and the line breaks
 * around them instead of diving to the axis.
 */
function Sparkline({
  values,
  color,
  label,
}: {
  values: (number | null)[];
  color: string;
  label: string;
}) {
  const width = 70;
  const height = 26;
  const top = 3;
  const bottom = height - 3;

  const present = values.filter((value): value is number => value !== null);
  if (present.length < 2) return null;

  const min = Math.min(...present);
  const max = Math.max(...present);
  const span = max - min;
  const step = values.length > 1 ? width / (values.length - 1) : width;

  const pointAt = (value: number, index: number) => ({
    x: Number((index * step).toFixed(2)),
    // A flat series sits on the middle line rather than pretending to climb.
    y: Number((span === 0 ? (top + bottom) / 2 : bottom - ((value - min) / span) * (bottom - top)).toFixed(2)),
  });

  const segments: string[] = [];
  let currentSegment: string[] = [];
  values.forEach((value, index) => {
    if (value === null) {
      if (currentSegment.length > 1) segments.push(currentSegment.join(' '));
      currentSegment = [];
      return;
    }
    const point = pointAt(value, index);
    currentSegment.push(`${currentSegment.length === 0 ? 'M' : 'L'} ${point.x} ${point.y}`);
  });
  if (currentSegment.length > 1) segments.push(currentSegment.join(' '));
  if (segments.length === 0) return null;

  let lastIndex = -1;
  for (let index = 0; index < values.length; index += 1) {
    if (values[index] !== null) lastIndex = index;
  }
  const lastPoint = lastIndex >= 0 ? pointAt(values[lastIndex] as number, lastIndex) : null;

  return (
    <svg
      className="kpi-spark"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      {segments.map((segment, index) => (
        <path
          key={index}
          d={segment}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
      {lastPoint && <circle cx={lastPoint.x} cy={lastPoint.y} r="1.9" fill={color} />}
    </svg>
  );
}

function KpiCard({
  icon,
  iconStyle,
  value,
  name,
  period,
  spark,
  sparkColor,
  sparkLabel,
}: {
  icon: ReactNode;
  iconStyle: CSSProperties;
  value: string;
  name: string;
  period: string;
  spark: (number | null)[] | null;
  sparkColor: string;
  sparkLabel: string;
}) {
  const line = spark ? <Sparkline values={spark} color={sparkColor} label={sparkLabel} /> : null;
  return (
    <div className="card kpi">
      <div className="kpi-top">
        <span className="kpi-ic" style={iconStyle}>
          {icon}
        </span>
      </div>
      <div className={`kpi-val ${value === '—' ? 'kpi-val-empty' : ''}`}>{value}</div>
      <div className="kpi-name">
        {name}
        <span className="kpi-period">{period}</span>
      </div>
      {line || <span className="kpi-spark-empty">{NOT_ENOUGH_DATA}</span>}
    </div>
  );
}

export default function Dashboard({ setActiveView }: DashboardProps) {
  const [range, setRange] = useState('14');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [recentDialogs, setRecentDialogs] = useState<any[]>([]);
  const [dialogsFetchedAt, setDialogsFetchedAt] = useState<Date | null>(null);
  // Re-render on language change so dates and numbers follow the account setting.
  useUserSettings();

  const load = useCallback(() => {
    setStatsError(null);
    fetchApi(`/stats/dashboard?days=${range}`)
      .then((data) => setStats(data))
      .catch((error: unknown) => {
        setStats(null);
        setStatsError(error instanceof Error ? error.message : 'Не удалось загрузить показатели');
      });

    fetchApi('/conversations')
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        setRecentDialogs(list.slice(0, 3));
        setDialogsFetchedAt(new Date());
      })
      .catch(() => {
        setRecentDialogs([]);
        setDialogsFetchedAt(null);
      });
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  if (statsError) {
    return (
      <section className="view active" id="view-dashboard">
        <div className="page-head">
          <div>
            <h1>Дашборд</h1>
            <p>Показатели не загрузились</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body dash-empty">
            <div>
              {statsError}
              <br />
              <button className="btn btn-outline btn-sm" style={{ marginTop: 12 }} onClick={load}>
                Повторить
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!stats) return <div style={{ padding: 40 }}>Загрузка дашборда...</div>;

  const days = stats.period?.days ?? Number(range);
  const label = periodLabel(days);
  const { leads, aiMessages, revenue, conversion } = stats.metrics;
  const allChannelTypes = Object.keys(CHANNELS);
  const connected = stats.channels?.connected ?? [];
  const missing = allChannelTypes.filter((type) => !connected.includes(type));

  const renderAreaChart = () => {
    const data = stats.series;
    const hasAnything = data.some((point) => point.n > 0);
    if (!data || data.length === 0 || !hasAnything) {
      return (
        <div className="dash-empty">
          {label === 'за 7 дней'
            ? 'За последние 7 дней новых обращений не было'
            : `Новых обращений ${label} не было`}
        </div>
      );
    }

    const maxVal = Math.max(...data.map((point) => point.n), 1);
    const width = 600;
    const height = 180;
    const paddingLeft = 30;
    const paddingRight = 15;
    const paddingTop = 15;
    const paddingBottom = 25;

    const points = data.map((item, index) => {
      const x = paddingLeft + (index * (width - paddingLeft - paddingRight)) / (data.length - 1 || 1);
      const y = height - paddingBottom - (item.n * (height - paddingTop - paddingBottom)) / maxVal;
      return { x, y };
    });

    const linePath = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
    const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - paddingBottom} L ${points[0].x} ${height - paddingBottom} Z`;
    const tickEvery = Math.max(1, Math.ceil(data.length / 5));

    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height: '100%', display: 'block' }}
        role="img"
        aria-label={`Новые обращения по дням ${label}`}
      >
        <defs>
          <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.3" />
            <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <line
          x1={paddingLeft}
          y1={height - paddingBottom}
          x2={width - paddingRight}
          y2={height - paddingBottom}
          stroke="var(--border)"
          strokeWidth="1"
        />
        <line
          x1={paddingLeft}
          y1={paddingTop}
          x2={width - paddingRight}
          y2={paddingTop}
          stroke="var(--border2)"
          strokeWidth="1"
          strokeDasharray="4"
        />
        <line
          x1={paddingLeft}
          y1={(height - paddingBottom + paddingTop) / 2}
          x2={width - paddingRight}
          y2={(height - paddingBottom + paddingTop) / 2}
          stroke="var(--border2)"
          strokeWidth="1"
          strokeDasharray="4"
        />
        <text x={paddingLeft - 8} y={paddingTop + 4} fontSize="9" fill="var(--muted)" textAnchor="end">
          {maxVal}
        </text>
        <text
          x={paddingLeft - 8}
          y={(height - paddingBottom + paddingTop) / 2 + 4}
          fontSize="9"
          fill="var(--muted)"
          textAnchor="end"
        >
          {Math.round(maxVal / 2)}
        </text>
        <text x={paddingLeft - 8} y={height - paddingBottom + 4} fontSize="9" fill="var(--muted)" textAnchor="end">
          0
        </text>
        <path d={areaPath} fill="url(#chartGrad)" />
        <path
          d={linePath}
          fill="none"
          stroke="var(--primary)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {points.map((point, index) => (
          <g key={data[index].d}>
            <circle cx={point.x} cy={point.y} r="3.5" fill="var(--card)" stroke="var(--primary)" strokeWidth="2" />
            {data[index].n > 0 && (
              <text x={point.x} y={point.y - 7} fontSize="9" fontWeight="bold" fill="var(--text)" textAnchor="middle">
                {data[index].n}
              </text>
            )}
            {(index === 0 || index === data.length - 1 || index % tickEvery === 0) && (
              <text x={point.x} y={height - 8} fontSize="9" fill="var(--muted)" textAnchor="middle">
                {formatDate(`${data[index].d}T12:00:00`, { day: 'numeric', month: 'short' })}
              </text>
            )}
          </g>
        ))}
      </svg>
    );
  };

  const renderDonutChart = () => {
    const data = stats.byChannel;
    const total = data.reduce((sum, item) => sum + item.n, 0);
    if (total === 0) {
      return (
        <div className="donut-box" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: 'var(--muted)', fontSize: 13 }}>Нет обращений {label}</span>
        </div>
      );
    }
    const r = 36;
    const circ = 2 * Math.PI * r;
    let accumulatedPercent = 0;
    return (
      <div className="donut-box">
        <svg width="132" height="132" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="50" cy="50" r={r} fill="transparent" stroke="var(--border2)" strokeWidth="11" />
          {data.map((channel) => {
            const pct = channel.n / total;
            const color = CHANNELS[channel.type]?.color || '#cbd5e1';
            const strokeDasharray = `${pct * circ} ${circ}`;
            const strokeDashoffset = -accumulatedPercent * circ;
            accumulatedPercent += pct;
            return (
              <circle
                key={channel.type}
                cx="50"
                cy="50"
                r={r}
                fill="transparent"
                stroke={color}
                strokeWidth="11"
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap={pct > 0.05 ? 'round' : 'butt'}
                style={{ transition: 'stroke-dashoffset 0.5s ease' }}
              />
            );
          })}
        </svg>
        <div className="donut-center">
          <b>{total}</b>
          <span>{label}</span>
        </div>
      </div>
    );
  };

  const renderBanner = () => {
    if (stats.channels.total === 0) {
      return (
        <div className="dash-banner-facts">
          <div className="banner-facts-copy">
            <h2>Ни один источник обращений не подключён</h2>
            <p>
              Пока в Clientix не поступают сообщения, показатели на этом экране останутся пустыми — считать нечего.
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => setActiveView('integrations')}>
            Подключить канал →
          </button>
        </div>
      );
    }

    if (missing.length === 0) return null;

    const missingNames = missing.map((type) => CHANNELS[type]?.name || type).join(', ');
    return (
      <div className="dash-banner-facts">
        <div className="banner-facts-copy">
          <h2>
            Подключено источников: {connected.length} из {allChannelTypes.length}
          </h2>
          <p>
            {label} через них пришло {formatNumber(leads.value)}{' '}
            {plural(leads.value, ['обращение', 'обращения', 'обращений'])}
            {stats.byChannel.length > 0
              ? `, из них по каналам: ${stats.byChannel
                  .map((channel) => `${CHANNELS[channel.type]?.name || channel.type} — ${channel.n}`)
                  .join(', ')}`
              : ''}
            . Не подключены: {missingNames}.
          </p>
        </div>
        <button className="btn btn-outline" onClick={() => setActiveView('integrations')}>
          Настроить каналы →
        </button>
      </div>
    );
  };

  return (
    <section className="view active" id="view-dashboard">
      <div className="page-head">
        <div>
          <h1>Дашборд</h1>
          <p>
            Сводка на{' '}
            {formatDate(new Date(), { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <div className="head-actions">
          <button className="btn btn-outline" onClick={() => setActiveView('warehouse')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 8 12 3 3 8v8l9 5 9-5V8z" />
            </svg>
            Склад
          </button>
          <button className="btn btn-primary" onClick={() => setActiveView('chats')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a8 8 0 0 1-8 8H4l2.3-2.9A8 8 0 1 1 21 12z" />
            </svg>
            Открыть чаты
          </button>
        </div>
      </div>

      {renderBanner()}

      {/* One period switch for the whole screen: every number below it, KPIs
          included, is measured over exactly this window. */}
      <div className="range-chips" style={{ width: 'fit-content', marginBottom: 14 }} role="group" aria-label="Период показателей">
        {RANGES.map((option) => (
          <button
            key={option.value}
            className={`range-chip ${range === option.value ? 'active' : ''}`}
            aria-pressed={range === option.value}
            onClick={() => setRange(option.value)}
          >
            {option.chip}
          </button>
        ))}
      </div>

      <div className="kpi-grid">
        <KpiCard
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M19 8v6M22 11h-6" />
            </svg>
          }
          iconStyle={{ background: 'var(--blue-soft)', color: 'var(--blue)' }}
          value={formatNumber(leads.value)}
          name="Новых обращений"
          period={label}
          spark={leads.spark}
          sparkColor="var(--blue)"
          sparkLabel={`Новые обращения по дням ${label}`}
        />
        <KpiCard
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 3l1.9 4.6 4.6 1.9-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />
            </svg>
          }
          iconStyle={{ background: 'var(--primary-soft)', color: 'var(--primary)' }}
          value={formatNumber(aiMessages.value)}
          name="Ответов ИИ клиентам"
          period={label}
          spark={aiMessages.spark}
          sparkColor="var(--primary)"
          sparkLabel={`Ответы ИИ по дням ${label}`}
        />
        <KpiCard
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4H6zM3 6h18M16 10a4 4 0 0 1-8 0" />
            </svg>
          }
          iconStyle={{ background: 'var(--green-soft)', color: 'var(--green)' }}
          value={`${formatNumber(revenue.value)} ₽`}
          name="Сумма выигранных сделок"
          period={label}
          spark={revenue.spark}
          sparkColor="var(--green)"
          sparkLabel={`Сумма выигранных сделок по дням ${label}`}
        />
        <KpiCard
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 7 13.5 15.5 8.5 10.5 2 17" />
              <path d="M16 7h6v6" />
            </svg>
          }
          iconStyle={{ background: 'var(--amber-soft)', color: 'var(--amber)' }}
          value={conversion.value === null ? '—' : `${conversion.value}%`}
          name={
            conversion.value === null
              ? 'Конверсия обращений в сделки'
              : `Конверсия обращений в сделки (${conversion.converted} из ${conversion.leads})`
          }
          period={label}
          spark={conversion.spark}
          sparkColor="var(--amber)"
          sparkLabel={`Накопленная конверсия обращений в сделки ${label}`}
        />
      </div>

      <div className="dash-grid">
        <div className="card">
          <div className="card-head">
            <div>
              <h3>Новые обращения по дням</h3>
              <span className="sub">Все каналы суммарно · {label}</span>
              <span className="metric-def">
                Обращение — первое входящее сообщение в диалоге, поэтому постоянный клиент считается один раз, а не при
                каждом сообщении.
              </span>
            </div>
          </div>
          <div className="card-body" style={{ height: 190 }}>
            {renderAreaChart()}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <h3>Обращения по каналам</h3>
              <span className="sub">{label}</span>
            </div>
          </div>
          <div className="card-body donut-wrap">
            {renderDonutChart()}
            <div className="legend">
              {stats.byChannel.map((channel) => {
                const info = CHANNELS[channel.type] || { name: channel.type, color: '#cbd5e1' };
                return (
                  <div className="legend-row" key={channel.type}>
                    <span className="legend-dot" style={{ background: info.color }}></span>
                    <span>{info.name}</span>
                    <b>{channel.n}</b>
                  </div>
                );
              })}
              {stats.byChannel.length === 0 && (
                <span className="chart-legend-note">Каналы появятся здесь, когда через них придут обращения.</span>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <h3>Последние диалоги</h3>
              <span className="sub">
                По времени последнего сообщения
                {dialogsFetchedAt
                  ? ` · загружено в ${formatDate(dialogsFetchedAt, { hour: '2-digit', minute: '2-digit' })}`
                  : ''}
              </span>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setActiveView('chats')}>
              Все чаты →
            </button>
          </div>
          <div className="card-body">
            {recentDialogs.length === 0 && <div className="dash-empty">Диалогов пока нет</div>}
            {recentDialogs.map((dialog) => (
              <div className="lead-row" key={dialog.id}>
                <div className="avatar">{(dialog.contactName || '?')[0]}</div>
                <div className="lead-info">
                  <b>{dialog.contactName}</b>
                  <span>{dialog.last_text || 'Сообщений пока нет'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
